/**
 * Vault zero-knowledge — crypto 100% côté client via Web Crypto API.
 *
 * - Dérivation de clé : PBKDF2-SHA256, 600 000 itérations (OWASP 2023+).
 * - Chiffrement : AES-256-GCM, nonce 12 bytes, auth tag 16 bytes séparé.
 * - La passphrase n'est jamais envoyée au serveur. Elle reste dans le navigateur
 *   ou dans le fragment URL (#) qui n'est pas transmis dans les requêtes HTTP.
 *
 * Le serveur stocke seulement : ciphertext, salt, nonce, auth_tag + métadonnées.
 * Un compromis serveur ne donne rien — il faudrait aussi la passphrase.
 */

const KDF_ITER = 600_000;
const SALT_LEN = 16;
const NONCE_LEN = 12;

function b64urlEncode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function b64urlDecode(s: string): Uint8Array {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const std = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export interface EncryptedPayload {
  ciphertext_b64: string;
  client_salt_b64: string;
  nonce_b64: string;
  auth_tag_b64: string;
  kdf_algo: 'pbkdf2-sha256';
  kdf_params: { iterations: number };
}

export async function encryptSecret(plaintext: string, passphrase: string): Promise<EncryptedPayload> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  const key = await deriveKey(passphrase, salt, KDF_ITER);
  const pt = new TextEncoder().encode(plaintext);
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as unknown as BufferSource, tagLength: 128 }, key, pt),
  );
  // Web Crypto AES-GCM retourne ciphertext || authTag (16 derniers bytes)
  const ciphertext = ctWithTag.slice(0, ctWithTag.length - 16);
  const authTag = ctWithTag.slice(ctWithTag.length - 16);
  return {
    ciphertext_b64: b64urlEncode(ciphertext),
    client_salt_b64: b64urlEncode(salt),
    nonce_b64: b64urlEncode(nonce),
    auth_tag_b64: b64urlEncode(authTag),
    kdf_algo: 'pbkdf2-sha256',
    kdf_params: { iterations: KDF_ITER },
  };
}

export interface DecryptInput {
  ciphertext_b64: string;
  client_salt_b64: string;
  nonce_b64: string;
  auth_tag_b64: string;
  kdf_algo: 'pbkdf2-sha256' | 'argon2id';
  kdf_params: { iterations?: number };
}

export async function decryptSecret(payload: DecryptInput, passphrase: string): Promise<string> {
  if (payload.kdf_algo !== 'pbkdf2-sha256') {
    throw new Error(`KDF ${payload.kdf_algo} non supporté par ce client`);
  }
  const salt = b64urlDecode(payload.client_salt_b64);
  const nonce = b64urlDecode(payload.nonce_b64);
  const ciphertext = b64urlDecode(payload.ciphertext_b64);
  const authTag = b64urlDecode(payload.auth_tag_b64);
  const iter = payload.kdf_params.iterations ?? KDF_ITER;
  const key = await deriveKey(passphrase, salt, iter);
  // Concat ciphertext || authTag pour Web Crypto
  const combined = new Uint8Array(ciphertext.length + authTag.length);
  combined.set(ciphertext, 0);
  combined.set(authTag, ciphertext.length);
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce as unknown as BufferSource, tagLength: 128 },
      key,
      combined as unknown as BufferSource,
    );
    return new TextDecoder().decode(plain);
  } catch {
    throw new Error('Passphrase invalide ou contenu altéré');
  }
}

/** Génère une passphrase aléatoire lisible (4 mots séparés par tirets). */
const WORDS = [
  'orage', 'bleu', 'pierre', 'forêt', 'soleil', 'lune', 'rivière', 'montagne',
  'aigle', 'tigre', 'phare', 'vague', 'roche', 'flèche', 'voile', 'éclair',
  'mystique', 'silence', 'aube', 'crépuscule', 'rapide', 'profond', 'cuivre', 'jade',
  'quartz', 'cristal', 'volcan', 'glacier', 'tempête', 'brise', 'tonnerre', 'écho',
];
export function generatePassphrase(): string {
  const buf = crypto.getRandomValues(new Uint16Array(4));
  const parts: string[] = [];
  for (let i = 0; i < 4; i++) parts.push(WORDS[buf[i]! % WORDS.length]!);
  parts.push(String((buf[0]! % 90) + 10));
  return parts.join('-');
}
